/*
 * lux-hours.js  ·  the live "open now / closed" state (Tsoulfa, 2026-08-13)
 *
 * Reads the schedule from #lux-hours-data, which snippets/lux-hours.liquid renders from
 * the single source of truth. Nothing here knows an hour, a day name or a phone number.
 *
 * THREE TRAPS THIS FILE EXISTS TO AVOID
 *
 * 1. Device time lies. A customer in Moscow at 21:00 is at 20:00 in Cyprus and we are
 *    still open; new Date().getHours() would tell them we are closed and cost the call.
 *    Russian and Hebrew are two of our four storefront languages, so foreign clocks are
 *    the normal case, not the edge case. Everything below runs through Europe/Nicosia,
 *    which also carries Cyprus daylight saving for free.
 *
 * 2. Server time freezes. Shopify caches rendered Liquid, so the same computed "open"
 *    can be served for hours. This has to run in the browser, and has to re-check while
 *    the page sits open, or a page loaded at 20:25 still claims we are open at 21:00.
 *
 * 3. Silence is not the same as closed. If Intl or the JSON fails, we do NOT guess. The
 *    badge keeps the plain static window that Liquid already rendered. The one state we
 *    never invent is "open", because a call that rings out costs more than no badge.
 */
(function () {
  'use strict';

  var node = document.getElementById('lux-hours-data');
  if (!node) return;

  var DATA;
  try {
    DATA = JSON.parse(node.textContent);
  } catch (e) {
    return; // malformed data: keep the honest static fallback
  }
  if (!DATA || !DATA.t) return;

  var T = DATA.t;

  /* ---------------------------------------------------------------------
   * "Now", as Cyprus sees it.
   * Returns { iso: '2026-08-13', dow: 4, mins: 1234 } or null if unavailable.
   * ------------------------------------------------------------------- */
  function nicosiaNow() {
    var parts;
    try {
      parts = new Intl.DateTimeFormat('en-US', {
        timeZone: DATA.tz || 'Europe/Nicosia',
        hour12: false,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      }).formatToParts(new Date());
    } catch (e) {
      return null; // no Intl time zone support: stay silent, keep the static text
    }

    var get = function (type) {
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === type) return parts[i].value;
      }
      return null;
    };

    var y = parseInt(get('year'), 10);
    var mo = parseInt(get('month'), 10);
    var d = parseInt(get('day'), 10);
    var h = parseInt(get('hour'), 10);
    var mi = parseInt(get('minute'), 10);
    if (isNaN(y) || isNaN(mo) || isNaN(d) || isNaN(h) || isNaN(mi)) return null;

    // Some engines render midnight as hour 24 under hour12:false. Left alone it makes
    // 00:15 look like 24:15, which is past every closing time, so the site would report
    // closed correctly by luck at midnight but open wrongly nowhere. Normalise anyway.
    if (h === 24) h = 0;

    return {
      iso: pad(y, 4) + '-' + pad(mo, 2) + '-' + pad(d, 2),
      // Day of week must come from the CYPRUS calendar date, never the device's.
      dow: new Date(Date.UTC(y, mo - 1, d)).getUTCDay(),
      mins: h * 60 + mi
    };
  }

  function pad(n, width) {
    var s = String(n);
    while (s.length < width) s = '0' + s;
    return s;
  }

  function toMins(hhmm) {
    var bits = String(hhmm).split(':');
    return parseInt(bits[0], 10) * 60 + parseInt(bits[1], 10);
  }

  // Calendar arithmetic in UTC so a daylight-saving jump cannot skip or repeat a day.
  function addDays(iso, n) {
    var b = iso.split('-');
    var dt = new Date(Date.UTC(parseInt(b[0], 10), parseInt(b[1], 10) - 1, parseInt(b[2], 10) + n));
    return dt.toISOString().slice(0, 10);
  }

  function isClosedDay(iso) {
    return !!(DATA.closed && DATA.closed.indexOf(iso) !== -1);
  }

  /* ---------------------------------------------------------------------
   * State for one schedule.
   * ------------------------------------------------------------------- */
  function stateFor(schedule, now) {
    if (!schedule) return null;

    var holidayToday = isClosedDay(now.iso);
    var today = holidayToday ? null : schedule[now.dow];

    if (today) {
      var open = toMins(today[0]);
      var close = toMins(today[1]);
      if (now.mins >= open && now.mins < close) {
        return { open: true, closesAt: today[1], holiday: false };
      }
      if (now.mins < open) {
        return { open: false, opensAt: today[0], sameDay: true, holiday: false };
      }
    }

    // Closed for today. Find the next day we actually answer, skipping holidays.
    for (var i = 1; i <= 8; i++) {
      var iso = addDays(now.iso, i);
      if (isClosedDay(iso)) continue;
      var dow = (now.dow + i) % 7;
      var slot = schedule[dow];
      if (slot) {
        return {
          open: false,
          opensAt: slot[0],
          opensDow: dow,
          sameDay: false,
          holiday: holidayToday
        };
      }
    }
    return { open: false, holiday: holidayToday };
  }

  function fill(template, time, day) {
    if (!template) return '';
    var out = String(template);
    if (time != null) out = out.split('__TIME__').join(time);
    if (day != null) out = out.split('__DAY__').join(day);
    return out;
  }

  function dayName(dow) {
    return (T.days && T.days[dow]) || '';
  }

  /* ---------------------------------------------------------------------
   * Phone badge: status, then what to do about it.
   * Out of hours the badge itself becomes the WhatsApp route, so the lead is
   * captured instead of lost to a phone nobody is holding. That link lives in
   * our own markup, so it never fights the theme's button styles.
   * ------------------------------------------------------------------- */
  /*
   * Copy is assembled from whole clauses joined with ", ". Locale strings carry no
   * trailing punctuation, and the split is by POSITION: a status clause opens the
   * sentence and is capitalised, a detail clause follows and is lowercase. That rule
   * survives Greek, Russian and Hebrew, which sentence-fragment concatenation does not.
   */
  function phoneText(st) {
    if (st.open) {
      return { text: [T.open_now, fill(T.answer_until, st.closesAt)].join(', '), open: true };
    }
    // On a holiday the holiday IS the status. "Closed now, today is a public holiday"
    // says the same thing twice and reads like an apology.
    var parts = [st.holiday ? T.holiday : T.closed_now];
    if (st.opensAt) {
      parts.push(st.sameDay
        ? fill(T.open_at, st.opensAt)
        : fill(T.open_day_at, st.opensAt, dayName(st.opensDow)));
    }
    return { text: parts.join(', '), open: false };
  }

  function showroomText(st) {
    if (st.open) return { text: fill(T.show_open_until, st.closesAt), open: true };
    // "Closed today" is only true when we do NOT open again today. At 07:00 the
    // showroom is shut but opens at 10:00, and saying "closed today" loses that visit.
    var parts = [st.holiday ? T.holiday : (st.sameDay ? T.closed_now : T.show_closed_today)];
    if (st.opensAt) {
      parts.push(st.sameDay
        ? fill(T.open_at, st.opensAt)
        : fill(T.show_opens, st.opensAt, dayName(st.opensDow)));
    }
    return { text: parts.join(', '), open: false };
  }

  function render() {
    var now = nicosiaNow();
    if (!now) return; // keep the static fallback

    var badges = document.querySelectorAll('[data-lux-hours-badge]');
    if (!badges.length) return;

    for (var i = 0; i < badges.length; i++) {
      var badge = badges[i];
      var kind = badge.getAttribute('data-lux-hours-badge') === 'showroom' ? 'showroom' : 'phone';
      var st = stateFor(DATA[kind], now);
      if (!st) continue;

      var copy = kind === 'phone' ? phoneText(st) : showroomText(st);
      var textEl = badge.querySelector('.lux-hours__badge-text');
      if (!textEl) continue;

      textEl.textContent = copy.text;
      badge.setAttribute('data-state', st.open ? 'open' : 'closed');

      // Out of hours, offer the route that always works. Phone only: nobody wants a
      // WhatsApp prompt attached to a showroom appointment line they just read.
      var existing = badge.querySelector('.lux-hours__wa');
      if (kind === 'phone' && !st.open && DATA.wa && T.whatsapp_cta) {
        if (!existing) {
          var a = document.createElement('a');
          a.className = 'lux-hours__wa';
          a.href = DATA.wa;
          a.rel = 'noopener';
          a.textContent = T.whatsapp_cta;
          badge.appendChild(a);
        }
      } else if (existing) {
        existing.parentNode.removeChild(existing);
      }
    }
  }

  render();

  // A page left open crosses 20:30 eventually. Re-check every minute, and again when the
  // tab comes back, because background timers are throttled or frozen while hidden.
  setInterval(render, 60000);
  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) render();
  });
})();
